import { sm4 } from 'sm-crypto';

// SM4密钥，16字节
const DEFAULT_KEY = '0123456789abcdeffedcba9876543210';

/**
 * 判断字符串是否为加密数据
 * @param str 需要判断的字符串
 * @returns boolean
 */
export const isEncrypted = (str: string): boolean => {
  try {
    // 检查是否为base64编码
    const decoded = atob(str);
    // 检查解码后的字符串是否符合加密数据的特征
    return decoded.length > 16 && /^[0-9a-fA-F]+$/.test(decoded);
  } catch {
    return false;
  }
};

/**
 * SM4加密
 * @param data 需要加密的数据
 * @param key 密钥，可选，默认使用DEFAULT_KEY
 * @returns 加密后的字符串（base64编码）
 */
export const encrypt = (data: string, key: string = DEFAULT_KEY): string => {
  if (!data) return '';
  const encrypted = sm4.encrypt(data, key);
  return btoa(encrypted);
};

/**
 * SM4解密
 * @param encryptedData 加密后的数据（base64编码）
 * @param key 密钥，可选，默认使用DEFAULT_KEY
 * @returns 解密后的字符串，如果解密失败则返回原字符串
 */
export const decrypt = (encryptedData: string, key: string = DEFAULT_KEY): string => {
  if (!encryptedData) return '';

  // 如果不是加密数据，直接返回原字符串
  if (!isEncrypted(encryptedData)) {
    return encryptedData;
  }

  try {
    const encrypted = atob(encryptedData);
    return sm4.decrypt(encrypted, key);
  } catch {
    return encryptedData;
  }
};

/**
 * URL参数加密
 * @param params URL参数对象
 * @param key 密钥，可选
 * @returns 加密后的URL参数字符串
 */
export const encryptUrlParams = (params: Record<string, any>, key?: string): string => {
  const paramsStr = JSON.stringify(params);
  return encrypt(paramsStr, key);
};

/**
 * URL参数解密
 * @param encryptedStr 加密后的URL参数字符串
 * @param key 密钥，可选
 * @returns 解密后的参数对象，如果解密失败则返回null
 */
export const decryptUrlParams = (
  encryptedStr: string,
  key?: string,
): Record<string, any> | null => {
  try {
    const decrypted = decrypt(encryptedStr, key);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
};
